import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Box, Pressable, Text, makeUclComponent } from '@rbilabs/universal-components';
import { usePathname } from 'expo-router';
import { Platform } from 'react-native';
import { IFormatCurrencyProps, formatCurrency } from 'utils';

import { IBaseProps } from '@rbi-ctg/frontend';
import useEffectOnce from 'hooks/use-effect-once';
import { usePrevious } from 'hooks/use-previous';
import { useAuthContext } from 'state/auth';
import { useLocale } from 'state/intl';
import { PROD_DEFAULT_REGION } from 'state/intl/constants';
import { LANGUAGES, LOCALES } from 'state/intl/types';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
// TODO: RN - locale switching -- shouldn't this pull from config?
import { Region } from 'utils/environment/types';
import { ISOs, ISOsToRegions, getCountryAndCurrencyCodes } from 'utils/form/constants';
import { routes } from 'utils/routing';
import { setIsWebViewVisible } from 'utils/ui';

import {
  FormatCurrencyForType,
  GetFormatCurrenctForLocaleOptions,
  GetFormatCurrenctForUserOptions,
  IDialogStaticPageState,
  IUIContext,
} from './types';

export const getFormatCurrencyForUser = ({
  userISOCountryCode,
  locale,
  region,
}: GetFormatCurrenctForUserOptions) => (amount: number) => {
  const { currencyCode, countryCode } = getCountryAndCurrencyCodes(ISOs[userISOCountryCode]);

  const formatOptions: IFormatCurrencyProps = {
    language: locale,
    currency: currencyCode,
    // React native does not currently suport the `narrowSymbol` option
    // with their Hermes engine.
    currencyDisplay:
      Platform.OS === 'web' ? (countryCode === region ? 'narrowSymbol' : 'symbol') : 'symbol',
    amount,
  };

  return formatCurrency(formatOptions);
};

export const getFormatCurrencyForLocale = ({
  userISOCountryCode,
  language,
  region,
}: GetFormatCurrenctForLocaleOptions) => (amount: number) => {
  const userRegion = ((userISOCountryCode && ISOsToRegions[userISOCountryCode]) ||
    PROD_DEFAULT_REGION) as Region;

  const userLocale = `${language}-${userRegion}` as LOCALES;

  const { currencyCode } = getCountryAndCurrencyCodes(ISOs[region]);

  const formatOptions: IFormatCurrencyProps = {
    language: userLocale,
    currency: currencyCode,
    // React native does not currently suport the `narrowSymbol` option
    // with their Hermes engine.
    currencyDisplay:
      Platform.OS === 'web' ? (userRegion === region ? 'narrowSymbol' : 'symbol') : 'symbol',
    amount,
  };

  return formatCurrency(formatOptions);
};

export const UIContext = createContext<IUIContext>({} as IUIContext);
export const useUIContext = () => useContext(UIContext);

export const UIProvider = ({ children }: IBaseProps) => {
  const { user } = useAuthContext();
  const { language, region, locale } = useLocale();
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [shouldShowPreConfirmCurbsideModal, setShouldShowPreConfirmCurbsideModal] = useState(false);
  const [confirmStoreOnCheckout, setConfirmStoreOnCheckout] = useState(true);
  const [isShowingStoreInfoDialog, setIsShowingStoreInfoDialog] = useState<boolean>(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const enableStoreConfirmationModal = useFlag(LaunchDarklyFlag.ENABLE_STORE_CONFIRMATION_MODAL);
  const [userISOCountryCode, setUserISOCountryCode] = useState('USA');
  const promoMessageHeightRef = useRef(0);
  const {
    handleSkipToMain,
    accessibilityMainContentRef,
    hasAccessibleMainContent,
    setHasAccessibleMainContent,
  } = useAccessibleMainContent();
  const [dialogStaticPageState, setDialogStaticPageState] = useState<IDialogStaticPageState>({
    page: '',
    isOpen: false,
  });

  const pathname = usePathname();
  const prevPathname = usePrevious<typeof routes[keyof typeof routes] | null>(pathname) ?? null;

  useEffect(() => {
    if (user?.details?.isoCountryCode) {
      setUserISOCountryCode(user.details.isoCountryCode);
    }
  }, [user]);

  useEffectOnce(() => {
    // Just in case there was an issue re-showing webview
    // Set isVisible on mount
    setIsWebViewVisible(true);
  });

  const showStoreInfoDialog = useCallback(() => {
    setIsShowingStoreInfoDialog(true);
  }, [setIsShowingStoreInfoDialog]);

  const hideStoreInfoDialog = useCallback(() => {
    setIsShowingStoreInfoDialog(false);
  }, [setIsShowingStoreInfoDialog]);

  const shouldConfirmStoreOnCheckout = useCallback(
    (flag: boolean) => {
      if (enableStoreConfirmationModal) {
        setConfirmStoreOnCheckout(flag);
      }
    },
    [setConfirmStoreOnCheckout, enableStoreConfirmationModal]
  );

  /**
   * Use the user profile's isoCountryCode to determine the locale.
   * Use the site's locale to determine currency.
   * Useful for product prices which are displayed in the currency of the site (e.g. show a US user '$CA' on the fr-CA and en-CA sites)
   */
  const formatCurrencyForLocale: FormatCurrencyForType = useCallback(
    getFormatCurrencyForLocale({ userISOCountryCode, region, language }),
    [userISOCountryCode, region, language]
  );

  /**
   * Use the site's locale as the formatted number's locale
   * Use the user profile's isoCountryCode to determine the currency,
   * Useful for showing user an amount that they are going to pay in their account's currency (e.g. reloading a pre-paid)
   */
  const formatCurrencyForUser: FormatCurrencyForType = useCallback(
    getFormatCurrencyForUser({ userISOCountryCode, locale, region }),
    [userISOCountryCode, locale, region]
  );

  const currencySymbol = useMemo(() => {
    return formatCurrencyForLocale(0)
      .replace(/\d+(,|\.)\d+/, '')
      .trim();
  }, [formatCurrencyForLocale]);

  const currencyIsSuffix = useMemo(() => {
    return language.toString() === LANGUAGES.fr.toString();
  }, [language]);

  const value = useMemo(
    () => ({
      userEmail,
      isShowingStoreInfoDialog,
      setUserEmail,
      hideStoreInfoDialog,
      showStoreInfoDialog,
      confirmStoreOnCheckout: enableStoreConfirmationModal && confirmStoreOnCheckout,
      shouldConfirmStoreOnCheckout,
      formatCurrencyForLocale,
      formatCurrencyForUser,
      currencySymbol,
      currencyIsSuffix,
      setIsWebViewVisible,
      dialogStaticPageState,
      setDialogStaticPageState,
      isUpdateModalOpen,
      setIsUpdateModalOpen,
      shouldShowPreConfirmCurbsideModal,
      setShouldShowPreConfirmCurbsideModal,
      promoMessageHeightRef,
      accessibilityMainContentRef,
      handleSkipToMain,
      hasAccessibleMainContent,
      setHasAccessibleMainContent,
      prevPathname,
    }),
    [
      confirmStoreOnCheckout,
      currencyIsSuffix,
      currencySymbol,
      enableStoreConfirmationModal,
      formatCurrencyForLocale,
      formatCurrencyForUser,
      hideStoreInfoDialog,
      isShowingStoreInfoDialog,
      shouldConfirmStoreOnCheckout,
      showStoreInfoDialog,
      userEmail,
      dialogStaticPageState,
      setDialogStaticPageState,
      isUpdateModalOpen,
      setIsUpdateModalOpen,
      shouldShowPreConfirmCurbsideModal,
      setShouldShowPreConfirmCurbsideModal,
      accessibilityMainContentRef,
      handleSkipToMain,
      hasAccessibleMainContent,
      setHasAccessibleMainContent,
      prevPathname,
    ]
  );

  return (
    <UIContext.Provider value={value}>
      {hasAccessibleMainContent ? (
        <PressableCustom onPress={handleSkipToMain}>
          <Text>Skip to main content</Text>
        </PressableCustom>
      ) : null}
      {children}
    </UIContext.Provider>
  );
};

// Let's keep this here for now to put all the accessibility logic in one place
const PressableCustom = makeUclComponent(Pressable).withConfig({
  position: 'absolute',
  left: -9999,
  zIndex: 999,
  tabIndex: 0,
  focusable: true,
  accessible: true,
  accessibilityRole: 'button',
  accessibilityLabel: 'Skip to main content',
});

/**
 * Hook that provides functionality to manage accessible main content region.
 * with this, an accessible user can skip to the main content of the page
 * */
export const useAccessibleMainContent = () => {
  const [hasAccessibleMainContent, setHasAccessibleMainContent] = useState(false);
  const accessibilityMainContentRef = useRef<any>(null);

  const handleSkipToMain = useCallback(() => {
    const element = accessibilityMainContentRef?.current;

    if (element) {
      element.setAttribute('tabIndex', '-1');
      element.setAttribute('focusable', 'true');

      const firstFocusableChild = element.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (firstFocusableChild) {
        (firstFocusableChild as HTMLElement).focus();
      } else if (typeof element.focus === 'function') {
        element.focus();
      }

      setTimeout(() => {
        element.removeAttribute('focusable');
        element.removeAttribute('tabIndex');
      }, 100);
    }
  }, []);

  return {
    accessibilityMainContentRef,
    handleSkipToMain,
    hasAccessibleMainContent,
    setHasAccessibleMainContent,
  };
};

/**
 * A wrapper component that makes its children accessible as the main content region.
 * Provides a focusable target for skip navigation and screen readers.
 * Used in conjunction with the useAccessibleMainContent
 */
export const AccessibleMainContent = ({ children }: { children: React.ReactNode }) => {
  const { accessibilityMainContentRef, setHasAccessibleMainContent } = useUIContext();

  useEffect(() => {
    setHasAccessibleMainContent(!!accessibilityMainContentRef.current);
  }, [accessibilityMainContentRef, setHasAccessibleMainContent]);

  return (
    <Box ref={accessibilityMainContentRef} focusable accessible tabIndex={0}>
      {children}
    </Box>
  );
};
